import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import TrustStrip, { EditorialMarquee } from "@/components/TrustStrip";
import About from "@/components/About";
import Services from "@/components/Services";
import WhyUs from "@/components/WhyUs";
import Process from "@/components/Process";
import Quote from "@/components/Quote";
import Faq from "@/components/Faq";
import FinalCta from "@/components/FinalCta";
import Footer from "@/components/Footer";
import ConsultModal from "@/components/ConsultModal";
import { PrivacyPolicy, TermsConditions } from "@/components/Legal";

function MobileCta({ onConsult, hidden }) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const onScroll = () => setPast(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {past && !hidden && (
        <motion.div
          initial={{ y: 90 }}
          animate={{ y: 0 }}
          exit={{ y: 90 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-4 bottom-4 z-40 md:hidden"
        >
          <button
            data-testid="mobile-sticky-cta"
            onClick={() => onConsult()}
            className="btn-gold w-full shadow-[0_8px_32px_rgba(201,163,78,0.35)]"
          >
            Book Free Consultation
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Home({ onConsult }) {
  return (
    <main>
      <Hero onConsult={onConsult} />
      <TrustStrip />
      <EditorialMarquee />
      <About />
      <Services onConsult={onConsult} />
      <WhyUs />
      <Process />
      <Quote onConsult={onConsult} />
      <Faq />
      <FinalCta onConsult={onConsult} />
    </main>
  );
}

function App() {
  const [consultOpen, setConsultOpen] = useState(false);
  const [presetService, setPresetService] = useState("");

  const openConsult = (service = "") => {
    setPresetService(service);
    setConsultOpen(true);
  };

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest('a[href^="/#"], a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute("href");
      const hash = href.startsWith("/#") ? href.slice(1) : href;
      if (hash.length > 1 && window.location.pathname === "/") {
        const el = document.querySelector(hash);
        if (el) {
          e.preventDefault();
          lenis.scrollTo(el, { offset: -72 });
        }
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <div className="App grain">
      <BrowserRouter>
        <Navbar onConsult={openConsult} />
        <Routes>
          <Route path="/" element={<Home onConsult={openConsult} />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
        </Routes>
        <Footer />
      </BrowserRouter>
      <MobileCta onConsult={openConsult} hidden={consultOpen} />
      <ConsultModal
        open={consultOpen}
        onOpenChange={setConsultOpen}
        presetService={presetService}
      />
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

export default App;
